import React from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, TextField, Chip } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import MuiButton from 'gui/components/common/MuiButton'

/**
 * AIMetadataField Component
 * Smart component that displays an AI-generated metadata field.
 * Automatically determines if the field is actionable (can be applied to form)
 * or display-only based on whether it matches a form field name.
 *
 * @param {Object} props
 * @param {string} props.fieldKey - The field key from AI response (e.g., 'shortDescription', 'seoTitle')
 * @param {*} props.value - The field value
 * @param {boolean} props.isActionable - Whether this field can be applied to the form
 * @param {boolean} props.isViewOnlyMode - Whether the form is in view-only mode
 * @param {Object} props.editableValues - Current editable values state
 * @param {Function} props.setEditableValues - Function to update editable values
 * @param {Function} props.onApply - Function to apply metadata to form (receives fieldKey, fieldKey)
 * @param {Object} props.appliedFields - Applied fields state for visual feedback
 */
function AIMetadataField ({
  fieldKey,
  value,
  isActionable,
  isViewOnlyMode,
  editableValues,
  setEditableValues,
  onApply,
  appliedFields,
}) {

  // Generate a nice label from the field key
  const generateLabel = (key) => {
    const lastPart = key.split('.').pop() // Get 'seoTitle' from 'seo.seoTitle'
    return lastPart
      .replace(/([A-Z])/g, ' $1') // Add space before capitals
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .trim()
  }

  const label = generateLabel(fieldKey)

  // Format value for editing - handle arrays by joining with commas
  const formatValueForEdit = (val) => {
    if (Array.isArray(val)) {
      return val.join(', ')
    }
    return val || ''
  }

  // Format value for display
  const formatValueForDisplay = (val) => {
    if (typeof val === 'object') {
      return JSON.stringify(val, null, 2)
    }
    return val?.toString() || ''
  }

  if (isActionable) {
    // ACTIONABLE FIELD - Show editable textarea with "Use" button
    const editValue = editableValues[fieldKey] ?? formatValueForEdit(value)

    return (
      <Box sx={{ mb: 2, p: 2, backgroundColor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0' }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          {label}:
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={editValue}
          onChange={(e) => setEditableValues(prev => ({ ...prev, [fieldKey]: e.target.value }))}
          disabled={isViewOnlyMode}
          variant="outlined"
          sx={{ mb: 2 }}
        />
        <MuiButton
          variant="contained"
          color="primary"
          size="small"
          onClick={() => onApply(fieldKey, fieldKey)}
          disabled={isViewOnlyMode || appliedFields[fieldKey]}
          startIcon={appliedFields[fieldKey] ? <CheckCircleIcon /> : null}
        >
          {appliedFields[fieldKey] ? 'Applied!' : `Use as ${label}`}
        </MuiButton>
      </Box>
    )
  }

  // DISPLAY-ONLY FIELD - Show as read-only
  // Special handling for string arrays - display as chips
  if (Array.isArray(value) && value.every(item => typeof item === 'string')) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          {label}:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {value.map((item, index) => (
            <Chip
              key={index}
              label={item}
              size="small"
              sx={{
                backgroundColor: '#e3f2fd',
                color: '#1976d2',
                fontWeight: 500,
              }}
            />
          ))}
        </Box>
      </Box>
    )
  }

  // For all other types, show as formatted text
  const displayValue = formatValueForDisplay(value)

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        {label}:
      </Typography>
      <Typography
        variant="body2"
        component="pre"
        sx={{
          whiteSpace: 'pre-wrap',
          fontFamily: typeof value === 'object' ? 'monospace' : 'inherit',
          fontSize: typeof value === 'object' ? '0.85rem' : 'inherit',
          mt: 0.5,
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  )
}

AIMetadataField.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  value: PropTypes.any.isRequired,
  isActionable: PropTypes.bool.isRequired,
  isViewOnlyMode: PropTypes.bool.isRequired,
  editableValues: PropTypes.object.isRequired,
  setEditableValues: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  appliedFields: PropTypes.object.isRequired,
}

export default AIMetadataField
